import React, { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { connect } from "react-redux";
import { compose } from "recompose";

import { Api } from "@api/index";
import { connectToApi } from "@api/components/connectToApi";
import { messages } from "@app/messages";
import { GlobalState } from "@app/models/state";
import { setTitle } from "@app/util";
import { Artist } from "@music/components/Artist";
import { messages as mMessages } from "@music/messages";
import { Album } from "@music/models";
import {
    PropsWithId,
    makeGetArtist,
    canViewArtist as canViewArtistSelector,
    canViewAlbum as canViewAlbumSelector,
} from "@music/selectors";
import { AlbumSalesTable } from "@sales/components/AlbumSalesTable";
import { DemographicsCharts } from "@sales/components/DemographicsCharts";
import { GeoSalesChart } from "@sales/components/GeoSalesChart";
import { PlaylistTrendsList } from "@sales/components/PlaylistTrendsList";
import { Sales } from "@sales/components/Sales";
import { messages as sMessages } from "@sales/messages";
import {
    canViewPlaylistTrends as canViewPlaylistTrendsSelector,
    canViewSales as canViewSalesSelector,
    canViewStreamDemographics as canViewStreamDemographicsSelector,
} from "@sales/selectors";
import { BreadcrumbsItem } from "@ui/Breadcrumbs";
import { Page, PageTitle } from "@ui/Page";
import { HasArtist, artistNotFound } from "@app/components/notFoundHocs";
import { Container } from "reactstrap";

interface ArtistsViewStateProps {
    canViewSales: boolean;
    canViewArtist: boolean;
    canViewAlbum: boolean;
    canViewPlaylistTrends: boolean;
    canViewStreamDemographics: boolean;
}

type Props = ArtistsViewStateProps & HasArtist & PropsWithId;

const ArtistsView = ({
    artist,
    canViewSales,
    canViewAlbum,
    canViewPlaylistTrends,
    canViewStreamDemographics,
}: Props) => {
    const intl = useIntl();
    useEffect(() => {
        setTitle(intl, messages.pageArtistsTitle, artist.name);
    }, [messages.pageArtistsTitle, artist.name]);
    const breadcrumbItems: BreadcrumbsItem[] = [{ text: artist.name}];

    return (
        <Page fluid className="artists-view-page" breadcrumbs={breadcrumbItems}>
            <div className="artists-view-page__details">
                <Artist artist={artist} style="headline" />
            </div>

            <Container>
                {canViewSales && <Sales artistId={artist.id} />}

                {canViewAlbum && canViewSales &&
                    <PageTitle className="display-3 mb-5" tag="h5">
                        <FormattedMessage {...mMessages.albumEntityPlural} />
                    </PageTitle>
                }
                {canViewAlbum && canViewSales &&
                    <AlbumSalesTable artistId={artist.id} albums={artist.albums as Album[]} />
                }
                {canViewSales && <GeoSalesChart artistId={artist.id} />}
                {canViewPlaylistTrends && (
                    <>
                        <PageTitle className="display-3 mb-5" tag="h5">
                            <FormattedMessage {...sMessages.playlistTrendsTitle} />
                        </PageTitle>
                        <PlaylistTrendsList artistId={artist.id} showTracks />
                    </>
            )}
                {canViewStreamDemographics &&
                    <DemographicsCharts artistId={artist.id} />
                }
            </Container>
        </Page>
    );
};

const makeArtistsShow = (id: number) => () => {
    return Api.artistsShow(id);
};

// Map the users state to the component.
const makeMapStateToProps = () => {
    const getArtist = makeGetArtist();
    return (state: GlobalState, ownProps: PropsWithId) => {
        const artist = getArtist(state, ownProps);
        return {
            artist,
            actionCreators: [ makeArtistsShow(Number(ownProps.params.id)) ],
            lastLoaded: [ artist ? artist.lastLoaded : null ],
            canViewSales: canViewSalesSelector(state),
            canViewArtist: canViewArtistSelector(state),
            canViewAlbum: canViewAlbumSelector(state),
            canViewPlaylistTrends: canViewPlaylistTrendsSelector(state),
            canViewStreamDemographics: canViewStreamDemographicsSelector(state),
        } as ArtistsViewStateProps;
    };
};

const ConnectedArtistsView = compose<Props, PropsWithId>(
    connect(makeMapStateToProps, null),
    connectToApi({
        showRefresh: false,
    }),
    artistNotFound,
)(ArtistsView);

export {
    ArtistsView as ArtistsViewPresenter,
    ConnectedArtistsView as ArtistsView,
    Props as ArtistsViewProps,
};
